import { Link } from "@tanstack/react-router"
import { Button } from "design-system/atoms/Button"
import { Input } from "design-system/atoms/Input"
import { MessageSquareText } from "lucide-react"
import { useCustomerNotesQuery } from "modules/notes/hooks/useCustomerNotesQuery"
import EmployeesFormSelector from "modules/settings/components/Team/Form/EmployeesFormSelector"
import { Loading } from "modules/shared/components/Loading"
import { useDeferredValue, useState } from "react"
import { useTranslation } from "react-i18next"
import { CustomerNoteResponseDataSchema } from "schema"
import z from "zod"
import { Note } from "./Note"

const PAGE_SIZE = 20

type Props = {
  customerId: string
}

type NoteItem = z.infer<typeof CustomerNoteResponseDataSchema>
type NoteGroup = {
  conversationId: string | null
  conversation: NoteItem["conversation"]
  notes: NoteItem[]
}

export function CustomerNotes({ customerId }: Props) {
  const { t } = useTranslation()
  const [total, setTotal] = useState(PAGE_SIZE)
  const [searchInput, setSearchInput] = useState("")
  const [employeeId, setEmployeeId] = useState<string | undefined>(undefined)

  const deferredQuery = useDeferredValue(searchInput)

  const { data, isPending, isFetching } = useCustomerNotesQuery({
    customerId,
    total,
    query: deferredQuery || undefined,
    employeeId,
  })

  const notes = data?.data ?? []
  const hasMore = !!data && notes.length < data.pageInfo.count
  const isFiltering = !!deferredQuery || !!employeeId

  if (isPending) return <Loading />

  const groups = notes.reduce<NoteGroup[]>((acc, note) => {
    const existing = acc.find((g) => g.conversationId === note.conversationId)
    if (existing) {
      existing.notes.push(note)
    } else {
      acc.push({
        conversationId: note.conversationId,
        conversation: note.conversation,
        notes: [note],
      })
    }
    return acc
  }, [])

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-4">
      <div className="flex gap-2">
        <Input
          placeholder={t("customers.notes.search")}
          value={searchInput}
          onChange={(e) => {
            setSearchInput(e.target.value)
            setTotal(PAGE_SIZE)
          }}
        />
        <EmployeesFormSelector
          value={employeeId ?? null}
          onChange={(option) => {
            setEmployeeId(option?.value)
            setTotal(PAGE_SIZE)
          }}
          placeholder={t("customers.notes.filterByEmployee")}
          emptyPlaceholder={t("customers.notes.filterByEmployeeEmpty")}
          searchPlaceholder={t("customers.notes.filterByEmployeeSearch")}
        />
      </div>
      {!notes.length && (
        <div className="flex flex-col gap-2 p-4 items-center justify-center text-center max-w-md mx-auto">
          <div className="flex flex-col gap-1">
            <span className="text-md">
              {isFiltering ? t("customers.notes.noNotesForFilters") : t("customers.notes.empty")}
            </span>
            <span className="text-sm text-muted-foreground">
              {isFiltering
                ? t("customers.notes.noNotesForFiltersDescription")
                : t("customers.notes.emptyDescription")}
            </span>
          </div>
        </div>
      )}
      <div className="group relative flex flex-col gap-4 overflow-y-auto flex-1 min-h-0">
        {groups.map((group) => {
          const hasConversationNode = group.conversationId !== null && group.conversation !== null
          const groupNodesCount = group.notes.length + (hasConversationNode ? 1 : 0)

          return (
            <div
              key={group.conversationId ?? `note-group-${group.notes[0]?.id}`}
              className="flex flex-col"
            >
              {hasConversationNode ? (
                <Link
                  to="/conversations/list/$customerId"
                  params={{ customerId }}
                  search={{ conversationId: Number(group.conversationId), tab: "conversations" }}
                  className="relative flex w-full cursor-pointer group/conv"
                >
                  <div className="absolute flex flex-col items-center left-4 top-0 mt-3 h-full z-10">
                    <div className="shrink-0 size-2.5 rounded-full bg-muted-foreground/40 z-10 transition-colors duration-150 group-hover/conv:bg-muted-foreground" />
                    {groupNodesCount > 1 && <div className="w-0.5 bg-border flex-1" />}
                  </div>
                  <div className="w-full py-2 pr-4 pl-12 flex items-center gap-2 rounded-lg transition-colors duration-150 group-hover/conv:bg-secondary">
                    <MessageSquareText className="size-3.5 text-muted-foreground shrink-0" />
                    <span className="text-xs font-medium text-muted-foreground truncate">
                      {group.conversation!.campaignName ?? t("conversations.table.noCampaign")}
                    </span>
                  </div>
                </Link>
              ) : null}

              {group.notes.map((note, noteIndex) => {
                const nodeIndex = noteIndex + (hasConversationNode ? 1 : 0)
                const isLastInGroup = nodeIndex === groupNodesCount - 1

                return (
                  <div key={note.id} className="group/item relative flex w-full">
                    <div className="absolute flex flex-col items-center left-4 top-0 mt-6 h-full z-10">
                      <div className="shrink-0 size-2.5 rounded-full bg-warning/40 z-10 transition-colors duration-150 group-hover/item:bg-warning" />
                      {!isLastInGroup && <div className="w-0.5 bg-border flex-1" />}
                    </div>
                    <div className="transition-colors duration-150 rounded-lg w-full py-2 pr-1 pl-12 h-auto items-start justify-start whitespace-normal">
                      <Note note={note} variant="plain" />
                    </div>
                  </div>
                )
              })}
            </div>
          )
        })}
        {hasMore && (
          <Button
            variant="outline"
            className="self-center text-muted-foreground"
            disabled={isFetching}
            onClick={() => setTotal((prev) => prev + PAGE_SIZE)}
          >
            {t("customers.notes.loadMore")}
          </Button>
        )}
      </div>
    </div>
  )
}
